<template>
  <transition name="slide-right" :appear="false">
    <div class="view-container">
      <div class="extended-filter-block">
        <h2>Отчеты</h2>
      </div>

      <div class="analytic-form">
        <div class="w-full">
          <div class="analytics-grid">
            <router-link
                v-for="analytic in visibleGeneralAnalytics"
                :key="analytic.id"
                :to="getAnalyticLink(analytic, 'general')"
                class="analytics-card-link"
                :class="{
                  'analytics-card-link--locked': isLocked(analytic, 'general'),
                }"
                @click="handleAnalyticClick($event, analytic, 'general')"
            >
              <a-card
                  :bordered="false"
                  class="analytics-card"
                  :class="{
                    'analytics-card--locked': isLocked(analytic, 'general'),
                  }"
              >
                <div class="analytics-card__header">
                  <div class="analytics-card__title-wrap">
                    <h3 class="analytics-card__title">
                      {{ analytic.name }}

                      <LockFilled
                          v-if="isLocked(analytic, 'general')"
                          class="analytics-card__lock"
                      />
                    </h3>

                    <a-tag
                        v-if="analytic.tag"
                        class="analytics-card__tag"
                    >
                      {{ analytic.tag }}
                    </a-tag>
                  </div>

                  <RightOutlined
                      v-if="!isLocked(analytic, 'general')"
                      class="analytics-card__arrow"
                  />
                </div>

                <p class="analytics-card__description">
                  {{ analytic.description }}
                </p>

                <div
                    v-if="isLocked(analytic, 'general')"
                    class="analytics-card__footer"
                >
                  <a-button
                      class="secondary-button analytics-card__button"
                      @click.stop.prevent="handleOrderForm(analytic.product_id)"
                  >
                    Оформить подписку
                  </a-button>
                </div>
              </a-card>
            </router-link>
          </div>

          <h2 class="analytics-section-title">
            Специальные отчеты
          </h2>

          <div class="analytics-grid analytics-grid--special">
            <router-link
                v-for="analytic in visibleSpecialAnalytics"
                :key="analytic.id"
                :to="getAnalyticLink(analytic, 'special')"
                class="analytics-card-link"
                :class="{
                  'analytics-card-link--locked': isLocked(analytic, 'special'),
                }"
                @click="handleAnalyticClick($event, analytic, 'special')"
            >
              <a-card
                  :bordered="false"
                  class="analytics-card analytics-card--special"
                  :class="{
                    'analytics-card--locked': isLocked(analytic, 'special'),
                  }"
              >
                <div class="analytics-card__header">
                  <div class="analytics-card__title-wrap">
                    <h3 class="analytics-card__title">
                      {{ analytic.name }}

                      <LockFilled
                          v-if="isLocked(analytic, 'special')"
                          class="analytics-card__lock"
                      />
                    </h3>

                    <a-tag
                        v-if="analytic.tag"
                        class="analytics-card__tag"
                    >
                      {{ analytic.tag }}
                    </a-tag>
                  </div>

                  <RightOutlined
                      v-if="!isLocked(analytic, 'special')"
                      class="analytics-card__arrow"
                  />
                </div>

                <p class="analytics-card__description">
                  {{ analytic.description }}
                </p>

                <div class="special-report-access-wrapper">
                  <div class="access-info">
                    <div class="access-info__item">
                      <span class="access-info__label">
                        Доступно:
                      </span>

                      <span class="access-info__value">
                        {{ availableReferenceProfiles.length }}
                      </span>
                    </div>

                    <a-divider
                        class="access-info__divider"
                        type="vertical"
                    />

                    <div class="access-info__item">
                      <span class="access-info__label">
                        Истекает:
                      </span>

                      <span class="access-info__value">
                        {{ referenceProfilesExpiresAt }}
                      </span>

                      <ClockCircleOutlined
                          v-if="referenceProfilesExpiresAt !== '-'"
                          class="access-info__icon"
                      />
                    </div>
                  </div>
                </div>

                <div
                    v-if="isLocked(analytic, 'special')"
                    class="analytics-card__footer analytics-card__footer--special"
                >
                  <a-button
                      class="secondary-button analytics-card__button"
                      @click.stop.prevent="handleOrderForm(analytic.product_id)"
                  >
                    Получить доступ
                  </a-button>
                </div>
              </a-card>
            </router-link>
          </div>

          <template v-if="isSuperAdmin && visibleAdminAnalytics.length">
            <h2 class="analytics-section-title">
              Отчеты для админа
            </h2>

            <div class="analytics-grid">
              <router-link
                  v-for="analytic in visibleAdminAnalytics"
                  :key="analytic.id"
                  :to="analytic.link"
                  class="analytics-card-link"
              >
                <a-card :bordered="false" class="analytics-card">
                  <div class="analytics-card__header">
                    <div class="analytics-card__title-wrap">
                      <h3 class="analytics-card__title">
                        {{ analytic.name }}
                      </h3>
                    </div>

                    <RightOutlined class="analytics-card__arrow" />
                  </div>

                  <p class="analytics-card__description">
                    {{ analytic.description }}
                  </p>
                </a-card>
              </router-link>
            </div>
          </template>
        </div>
      </div>

      <NewOrderForm
          v-if="showOrderForm"
          :open="showOrderForm"
          :initial-open-tables="['analytics']"
          @close="showOrderForm = false"
      />
    </div>
  </transition>
</template>

<script>
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {
  ClockCircleOutlined,
  LockFilled,
  RightOutlined,
} from '@ant-design/icons-vue'

import BaseReportParametres from '../../feature/analytics/report-parameters/BaseReportParametres.vue'
import BaseReport from '../../feature/analytics/reports/BaseReport.vue'
import GroupReportParametres from '../../feature/analytics/report-parameters/GroupReportParametres.vue'
import GroupReport from '../../feature/analytics/reports/GroupReport.vue'
import GeneralDynamicParametres from '../../feature/analytics/report-parameters/GeneralDynamicParametres.vue'
import ReferenceReportParametres from '../../feature/analytics/report-parameters/ReferenceReportParametres.vue'
import ReferenceUsersReportParametres from '../../feature/analytics/report-parameters/ReferenceUsersReportParametres.vue'
import RepeatedReportParametres from '../../feature/analytics/report-parameters/RepeatedReportParametres.vue'
import DemographicReportParametres from '../../feature/analytics/report-parameters/DemographicReportParametres.vue'
import DemographicReport from '../../feature/analytics/reports/DemographicReport.vue'
import ReferenceUsersReport from '../../feature/analytics/reports/ReferenceUsersReport.vue'
import CustomSelect2 from '../../components/Forms/Fields/CustomSelect2.vue'
import NewOrderForm from '@/views/Orders/OrderForm.vue'

import useCheckRights from '../../mixins/useCheckRights'
import analyticsReports from '../../mixins/dictionaries/analyticsReports'
import useBreakpoints from '@/mixins/useBreakpoints'
import {
  getReportAnalyticsAccessKey,
  hasSubscriptionAnalyticsAccess,
} from '@/utils/subscriptionAnalyticsAccess'

export default {
  name: 'analytic-form',

  components: {
    ClockCircleOutlined,
    NewOrderForm,
    CustomSelect2,
    BaseReportParametres,
    BaseReport,
    GroupReportParametres,
    GroupReport,
    GeneralDynamicParametres,
    ReferenceReportParametres,
    ReferenceUsersReportParametres,
    RepeatedReportParametres,
    DemographicReportParametres,
    DemographicReport,
    ReferenceUsersReport,
    RightOutlined,
    LockFilled,
  },

  setup() {
    const store = useStore()
    const { getRight } = useCheckRights()

    const reportType = ref(null)
    const reportRequest = ref(false)
    const groupId = ref(null)
    const showOrderForm = ref(false)
    const { isMobile } = useBreakpoints()

    const analytics = analyticsReports

    const winWidth = computed(() => store.state.width)

    const user = computed(() => store.state.profile.profile)
    const permittedSubscriptions = computed(() => store.state.subscription.permittedSubscriptions)

    const reportData = computed(() => store.state.analytics.reportData)

    const availableReferenceProfiles = computed(() => {
      return store.state.references.availableProfiles || []
    })

    const referenceProfilesExpiresAt = computed(() => {
      if (!availableReferenceProfiles.value.length) {
        return '-'
      }

      return availableReferenceProfiles.value[0]?.expires_at || '-'
    })

    const reportReservedData = computed(
        () => store.state.analytics.reportReservedData
    )

    const isSuperAdmin = computed(() => getRight('superadmin'))

    const canShowAnalytic = (analytic) => {
      return isSuperAdmin.value || analytic.name !== 'Данные по тестированиям'
    }

    const visibleGeneralAnalytics = computed(() => {
      return analytics.general.filter(canShowAnalytic)
    })

    const visibleSpecialAnalytics = computed(() => {
      return analytics.special.filter(canShowAnalytic)
    })

    const visibleAdminAnalytics = computed(() => {
      return analytics.admin.filter((analytic) => analytic.type === 'testing_data')
    })

    const isLocked = (analytic, type = 'general') => {
      if (type === 'special' || analytic.available) {
        return false;
      }

      return !hasSubscriptionAnalyticsAccess(
          permittedSubscriptions.value,
          getReportAnalyticsAccessKey(analytic.type)
      );
    }

    const getAnalyticLink = (analytic, type = 'general') => {
      return isLocked(analytic, type) ? '' : analytic.link;
    }

    const handleAnalyticClick = (event, analytic, type = 'general') => {
      if (isLocked(analytic, type)) {
        event.preventDefault();
      }
    }

    const handleOrderForm = (product) => {
      if (product) {
        store.dispatch('relations/setInsufficientProducts', {
          analytics: [
            {
              product_id: product,
            },
          ],
        })
      }

      showOrderForm.value = true
    }

    const print = () => {
      window.print()
    }

    const loadPermittedSubscriptions = () => {
      if (!permittedSubscriptions.value || !permittedSubscriptions.value.length) {
        if (getRight('subscription') && user.value?.id) {
          store.dispatch('subscription/getPermittedSubscriptions', user.value?.company?.id ?? null)
        }
      }
    }

    onBeforeMount(() => {
      if (!availableReferenceProfiles.value.length) {
        store.dispatch('references/getAvailableProfiles')
      }
    })

    watch(reportType, () => {
      reportRequest.value = false
      store.commit('analytics/setReportData', null)
    })

    watch(
        () => [user.value?.id, user.value?.company?.id],
        () => {
          loadPermittedSubscriptions()
        },
        { immediate: true }
    )

    return {
      reportType,
      reportRequest,
      print,
      winWidth,
      reportData,
      reportReservedData,
      getRight,
      groupId,
      analytics,
      isSuperAdmin,
      showOrderForm,
      handleOrderForm,
      isMobile,
      visibleGeneralAnalytics,
      visibleSpecialAnalytics,
      visibleAdminAnalytics,
      isLocked,
      getAnalyticLink,
      handleAnalyticClick,
      availableReferenceProfiles,
      referenceProfilesExpiresAt,
    }
  },
}
</script>

<style lang="scss" scoped>
.view-container {
  padding: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.2s ease-out;
}

.slide-right-leave-to,
.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-1000px);
}

.analytic-form {
  width: 100%;
}

.analytics-section-title {
  margin: 32px 0 16px;
  text-align: left;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.85);

  @media (max-width: $mobile-breakpoint) {
    padding: 0 20px;
  }
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 16px;
}

.analytics-card-link {
  display: block;
  min-width: 0;
  height: 100%;
  color: inherit;
  text-decoration: none;

  &:hover {
    color: inherit;
    text-decoration: none;

    .analytics-card__arrow {
      color: var(--main-green);
    }
  }

  &--locked {
    cursor: default;

    .analytics-card__title {
      color: rgba(0, 0, 0, 0.65);
    }

    &:hover {
      .analytics-card {
        box-shadow: none;
        transform: none;
      }
    }
  }
}

.analytics-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #ffffff;
  border-radius: 6px;
}

.analytics-card--special {
  min-height: 100px;
}

.analytics-card :deep(.ant-card-body) {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  box-shadow: none;
}

.analytics-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.analytics-card__title-wrap {
  min-width: 0;
}

.analytics-card__title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  text-align: left;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.analytics-card__tag {
  margin-top: 8px;
}

.analytics-card__lock {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.25);
  font-size: 16px;
}

.analytics-card__arrow {
  flex: 0 0 auto;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.25);
  font-size: 16px;
}

.analytics-card__description {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  text-align: left;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.analytics-card__footer {
  margin-top: auto;
  padding-top: 16px;
  text-align: start;
}

.analytics-card__footer--special {
  padding-top: 16px;
}

.analytics-card__button {
  margin-bottom: 0;
}

.special-report-access-wrapper {
  margin-top: 12px;
}

.access-info {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 0;
  width: 100%;
  text-align: left;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.access-info__item {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  min-width: 0;
}

.access-info__label {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.65);
}

.access-info__value {
  flex: 0 0 auto;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.access-info__divider {
  height: 16px;
  margin: 0 8px;
  border-color: rgba(0, 0, 0, 0.12);
}

.access-info__icon {
  flex: 0 0 auto;
  margin-left: 2px;
  color: #faad14;
  font-size: 16px;
}

.analytics-card__meta {
  margin-bottom: 8px;
  overflow: hidden;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.25;
  text-align: left;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.analytics-card__divider {
  margin: 0 4px;
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 25px;
    align-items: center;
    justify-content: space-between;
  }
}

.extended-filter-block h2 {
  margin: 0;
  text-align: left;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.85);
}

@media (max-width: $tablet-breakpoint) {
  .analytics-grid--special {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $mobile-breakpoint) {
  .analytics-grid {
    grid-template-columns: 1fr;
  }

  .analytics-card,
  .analytics-card--locked,
  .analytics-card--special,
  .analytics-card--special.analytics-card--locked {
    height: auto;
    min-height: 140px;
  }

  .analytics-card__button {
    width: 100%;
  }
}
</style>
